// src/components/BCList.js
import React, { useEffect, useState } from 'react';

// Utility: date formatting "02 Jan 2025" without TZ surprises
function formatDate(dateString) {
  if (!dateString) return '';
  const s = String(dateString);
  const iso = s.includes('T') ? s.split('T')[0] : s; // "YYYY-MM-DD"
  const [y, m, d] = iso.split('-');
  if (y && m && d) {
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${d} ${months[Number(m) - 1]} ${y}`;
  }
  try {
    return new Date(s).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, ' ');
  } catch {
    return '';
  }
}

export default function BCList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editIdx, setEditIdx] = useState(-1);
  const [editRow, setEditRow] = useState({});

  // Load list
  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    setError('');
    fetch('/api/bclist', { signal: ac.signal, cache: 'no-store' })
      .then(res => res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`)))
      .then(data => setRows(Array.isArray(data) ? data : []))
      .catch(err => {
        if (err.name !== 'AbortError') setError(err.message || 'Failed to load');
      })
      .finally(() => setLoading(false));
    return () => ac.abort();
  }, []);

  const handleEdit = (idx) => {
    setEditIdx(idx);
    setEditRow({ ...rows[idx] });
  };

  const handleCancel = () => {
    setEditIdx(-1);
    setEditRow({});
    setError('');
  };

  const handleChange = (field, value) => {
    setEditRow(prev => ({ ...prev, [field]: value }));
  };

  async function handleSave() {
    if (editIdx < 0) return;
    setSaving(true);
    setError('');

    // Some views name it BCNo, others CustomBCNo — support both
    const bcno = editRow.CustomBCNo ?? editRow.BCNo;
    if (!bcno) {
      setError('Missing BC number on the selected row.');
      setSaving(false);
      return;
    }

    // Date from <input type="date"> is YYYY-MM-DD or ''
    const RequestedDate = editRow.RequestedDate ? editRow.RequestedDate : null;

    try {
      const res = await fetch(`/api/bclist/${encodeURIComponent(bcno)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          RequesterName: editRow.RequesterName || null,
          RequesterDepartment: editRow.RequesterDepartment || null,
          RequestedDate,
          Remarks: editRow.Remarks || null
        })
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `HTTP ${res.status}`);
      }

      // Re-fetch the row list to reflect DB-side changes (triggers, computed cols, etc.)
      const fresh = await fetch('/api/bclist', { cache: 'no-store' }).then(r => r.json());
      setRows(Array.isArray(fresh) ? fresh : []);
      setEditIdx(-1);
      setEditRow({});
    } catch (err) {
      setError(err.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ padding: 18 }}>
      <h1 style={{ fontSize: 22, marginBottom: 8 }}>BC List</h1>
      {error && (
        <div style={{ color: '#d9534f', fontSize: 13, marginBottom: 8 }}>
          {error}
        </div>
      )}

      <div
        style={{
          background: '#f8faff',
          borderRadius: 8,
          marginTop: 10,
          overflowX: 'auto'
        }}
      >
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '13px',
            minWidth: 1200
          }}
        >
          <thead>
            <tr>
              <th style={th}>S. No</th>
              <th style={th}>Requester</th>
              <th style={th}>Department</th>
              <th style={th}>Date</th>
              <th style={th}>Form Type</th>
              <th style={th}>BC No</th>
              <th style={th}>AJU No</th>
              <th style={th}>BC Date</th>
              <th style={th}>Remarks</th>
              <th style={th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} style={td}>Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={10} style={td}>No data found.</td></tr>
            ) : (
              rows.map((row, idx) => (
                <tr key={`${row.CustomBCNo ?? row.BCNo ?? idx}`}>
                  <td style={td}>{idx + 1}</td>

                  <td style={td}>
                    {editIdx === idx ? (
                      <input
                        style={input}
                        value={editRow.RequesterName || ''}
                        onChange={(e) => handleChange('RequesterName', e.target.value)}
                      />
                    ) : (row.RequesterName || '')}
                  </td>

                  <td style={td}>
                    {editIdx === idx ? (
                      <input
                        style={input}
                        value={editRow.RequesterDepartment || ''}
                        onChange={(e) => handleChange('RequesterDepartment', e.target.value)}
                      />
                    ) : (row.RequesterDepartment || '')}
                  </td>

                  <td style={td}>
                    {editIdx === idx ? (
                      <input
                        type="date"
                        style={input}
                        value={editRow.RequestedDate ? String(editRow.RequestedDate).split('T')[0] : ''}
                        onChange={(e) => handleChange('RequestedDate', e.target.value)}
                      />
                    ) : (
                      formatDate(row.RequestedDate)
                    )}
                  </td>

                  <td style={td}>{row.FormType}</td>
                  <td style={td}>{row.CustomBCNo ?? row.BCNo}</td>
                  <td style={td}>{row.AJUNo}</td>
                  <td style={td}>{formatDate(row.BCDate)}</td>

                  <td style={td}>
                    {editIdx === idx ? (
                      <input
                        style={input}
                        value={editRow.Remarks || ''}
                        onChange={(e) => handleChange('Remarks', e.target.value)}
                      />
                    ) : (row.Remarks || '')}
                  </td>

                  <td style={td}>
                    {editIdx === idx ? (
                      <>
                        <button style={saveBtn} onClick={handleSave} disabled={saving}>
                          {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button style={cancelBtn} onClick={handleCancel} disabled={saving}>
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button style={editBtn} onClick={() => handleEdit(idx)}>
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const th = {
  padding: '7px 8px',
  background: '#f8faff',
  borderBottom: '1px solid #eee',
  textAlign: 'left',
  whiteSpace: 'nowrap',
  fontSize: '13px'
};
const td = {
  padding: '7px 8px',
  borderBottom: '1px solid #eee',
  background: '#fff',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: 140
};
const input = {
  fontSize: '12px',
  padding: '2px 5px',
  width: '110px',
  borderRadius: 4,
  border: '1px solid #bbb'
};
const editBtn = {
  fontSize: '12px',
  padding: '3px 10px',
  background: '#3b82f6',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer'
};
const saveBtn = {
  fontSize: '12px',
  padding: '3px 7px',
  marginRight: 3,
  background: '#22c55e',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer'
};
const cancelBtn = {
  fontSize: '12px',
  padding: '3px 7px',
  background: '#f87171',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer'
};
